import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from 'react';
import { useAppStore } from '../../state/store';
import { useSeriesOptions } from '../../utils/useSeriesOptions';
import { InstrumentPill, ValuePill, FromIndicatorBadge } from './IndicatorPreviewFields';

export type IndicatorEditorROCRef = {
  getConfig: () => {
    instrumentId: string;
    initialField: string;
    finalField: string;
    lookback: number;
    lag: number;
  };
};

type Props = {
  config: {
    instrumentId: string;
    initialField?: string;
    finalField?: string;
    lookback?: number;
    lag?: number;
  };
  onChange: (config: Props['config']) => void;
  editMode: boolean;
  indicatorId?: string;
};

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const IndicatorEditorROC = forwardRef<IndicatorEditorROCRef, Props>(
  ({ config, onChange, editMode, indicatorId }, ref) => {
    const [lookback, setLookback] = useState(config.lookback ?? 1);
    const [lag, setLag] = useState(config.lag ?? 0);

    const allSeriesOptions = useSeriesOptions(indicatorId);
    const hasSingleOption = allSeriesOptions.length === 1;

    useEffect(() => {
      if (hasSingleOption && config.instrumentId !== allSeriesOptions[0].id) {
        onChange({ ...config, instrumentId: allSeriesOptions[0].id });
      }
    }, [hasSingleOption, allSeriesOptions, config.instrumentId, onChange]);

    const selectedSeries = allSeriesOptions.find(s => s.id === config.instrumentId);
    const availableFields = selectedSeries
      ? Object.keys(selectedSeries.emittedFields).filter(
          f => selectedSeries.emittedFields[f] === 'Number'
        )
      : [];

    const initialField =
      config.initialField && availableFields.includes(config.initialField)
        ? config.initialField
        : availableFields.includes('Close')
          ? 'Close'
          : availableFields[0] || 'Value';
    const finalField =
      config.finalField && availableFields.includes(config.finalField)
        ? config.finalField
        : availableFields.includes('Close')
          ? 'Close'
          : availableFields[0] || 'Value';

    useEffect(() => {
      if (config.initialField !== initialField || config.finalField !== finalField) {
        onChange({ ...config, initialField, finalField });
      }
    }, [initialField, finalField, config, onChange]);

    const handleSeriesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange({ ...config, instrumentId: e.target.value });
    };

    const handleLookbackChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value);
      if (!isNaN(val) && val >= 1) {
        setLookback(val);
        onChange({ ...config, lookback: val });
      }
    };

    const handleLagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value);
      if (!isNaN(val) && val >= 0) {
        setLag(val);
        onChange({ ...config, lag: val });
      }
    };

    useImperativeHandle(ref, () => ({
      getConfig: () => ({
        instrumentId: config.instrumentId,
        initialField,
        finalField,
        lookback,
        lag,
      }),
    }));

    if (!editMode) {
      const inst = useAppStore.getState().instruments.find(i => i.id === config.instrumentId);
      const symbol = inst?.symbol || selectedSeries?.label || '???';
      return (
        <div className="flex flex-wrap gap-2 items-center text-sm">
          <InstrumentPill>{symbol}</InstrumentPill>
          <ValuePill label="Lookback" value={lookback} />
          {lag > 0 && <ValuePill label="Lag" value={lag} />}
          <ValuePill label="Initial" value={cap(initialField)} />
          <ValuePill label="Final" value={cap(finalField)} />
          {selectedSeries?.type === 'indicator' && <FromIndicatorBadge />}
        </div>
      );
    }

    return (
      <div className="flex flex-wrap gap-4 items-center">
        {!hasSingleOption && (
          <label>
            Series:
            <select
              value={config.instrumentId}
              onChange={handleSeriesChange}
              className="ml-2 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm"
            >
              {allSeriesOptions.map(opt => (
                <option key={opt.id} value={opt.id}>
                  {opt.label} {opt.type === 'indicator' ? ' (indicator)' : ''}
                </option>
              ))}
            </select>
          </label>
        )}

        {availableFields.length > 0 && (
          <>
            <label>
              Initial field:
              <select
                value={initialField}
                onChange={e => onChange({ ...config, initialField: e.target.value })}
                className="ml-2 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm"
              >
                {availableFields.map(f => (
                  <option key={f} value={f}>
                    {cap(f.toLowerCase())}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Final field:
              <select
                value={finalField}
                onChange={e => onChange({ ...config, finalField: e.target.value })}
                className="ml-2 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm"
              >
                {availableFields.map(f => (
                  <option key={f} value={f}>
                    {cap(f.toLowerCase())}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}

        <label>
          Lookback:
          <input
            type="number"
            min="1"
            value={lookback}
            onChange={handleLookbackChange}
            className="ml-2 w-20 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark"
          />
        </label>
        <label>
          Lag:
          <input
            type="number"
            min="0"
            value={lag}
            onChange={handleLagChange}
            className="ml-2 w-20 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark"
          />
        </label>
      </div>
    );
  }
);

IndicatorEditorROC.displayName = 'IndicatorEditorROC';

export default IndicatorEditorROC;
